/**
 * F-010 — Score domain.
 *
 * Tracks current run score plus a high-water mark across the session.
 * F-020 will persist highScore to LocalStorage. F-018 reads finalScore on
 * game-over. The class is event-emitting so UI can subscribe without
 * polling.
 */

export type ScoreListener = (snapshot: ScoreSnapshot) => void;

export interface ScoreSnapshot {
  current: number;
  highScore: number;
  merges: number;
}

export class Score {
  private _current = 0;
  private _high = 0;
  private _merges = 0;
  private listeners: Set<ScoreListener> = new Set();

  get current(): number {
    return this._current;
  }

  get highScore(): number {
    return this._high;
  }

  get merges(): number {
    return this._merges;
  }

  add(points: number, mergeCount = 1): void {
    if (points < 0) throw new Error("score.add: points must be non-negative");
    this._current += points;
    this._merges += mergeCount;
    if (this._current > this._high) this._high = this._current;
    this.emit();
  }

  /**
   * F-101 — soft penalty (orbit escape 등). 현재 점수만 감소시키고
   * highScore · merges 는 그대로. 0 미만으로는 안 내려감 (음수 점수 금지).
   */
  penalize(points: number): void {
    if (points < 0) throw new Error("score.penalize: points must be non-negative");
    this._current = Math.max(0, this._current - points);
    this.emit();
  }

  reset(): void {
    this._current = 0;
    this._merges = 0;
    this.emit();
  }

  /** Used by LocalStorage rehydrate (F-020). */
  setHighScore(value: number): void {
    if (value < 0) throw new Error("score: highScore must be non-negative");
    this._high = value;
    this.emit();
  }

  subscribe(listener: ScoreListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): ScoreSnapshot {
    return {
      current: this._current,
      highScore: this._high,
      merges: this._merges,
    };
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const l of this.listeners) l(snap);
  }
}
